// ============================================================================
//  ENDOR AURORA · Design System (MUI v4)
//  Arquivo: src/theme.js  — importado em src/App.js
//
//  Baseado no design system entregue pela Claude Designer, ESTENDIDO para
//  preservar todas as chaves legadas que o app já consome (calculatedLogo*,
//  palette.dark.main, fancyBackground, scrollbarStyles, etc.) — senão alguns
//  componentes quebram (tela branca). NÃO altera lógica/rotas.
// ============================================================================
import { createTheme } from "@material-ui/core/styles";

// ---- helper: alpha sobre cor hex (#RRGGBB + AA) — evita depender de libs ----
const a = (hex, alpha) => {
  const h = String(hex || "#000000").replace("#", "");
  const v = Math.round(Math.min(Math.max(alpha, 0), 1) * 255)
    .toString(16)
    .padStart(2, "0");
  return `#${h.length === 3 ? h.split("").map((c) => c + c).join("") : h}${v}`;
};

// ---- ESCALAS GLOBAIS (iguais nos dois modos) -------------------------------
const RADIUS = { sm: 10, md: 12, lg: 16, pill: 999 };
const MOTION = "200ms cubic-bezier(.4,0,.2,1)";

/**
 * @param {"light"|"dark"} mode
 * @param {string} primary  cor da marca (primaryColorLight/Dark do backend; default azul)
 * @param {object} legacy   { locale, appLogoLight, appLogoDark, appLogoFavicon, appName,
 *                            calculatedLogoLight, calculatedLogoDark }
 */
export default function buildTheme(mode = "light", primary = "#2563eb", legacy = {}) {
  const dark = mode === "dark";

  // ---- TOKENS DE SUPERFÍCIE / TEXTO / BORDA -------------------------------
  const c = dark
    ? {
        appBg: "#0b111d",
        paper: "#121a29",
        paper2: "#0e1626",
        elevated: "#1b2740",
        border: "#243149",
        text1: "#e9eef8",
        text2: "#9aa7bd",
        text3: "#5f6e88",
        scroll: "#2b3a57",
        glass: "rgba(15,22,38,0.74)",
      }
    : {
        appBg: "#edf1f8",
        paper: "#ffffff",
        paper2: "#f5f8fc",
        elevated: "#ffffff",
        border: "#e6eaf3",
        text1: "#19212e",
        text2: "#5a6678",
        text3: "#94a0b3",
        scroll: "#cdd6e6",
        glass: "rgba(255,255,255,0.74)",
      };

  // ---- ELEVAÇÃO (4 níveis por modo) ---------------------------------------
  const sh = dark
    ? [
        "none",
        "0 2px 8px -3px rgba(0,0,0,.5)",
        "0 8px 22px -8px rgba(0,0,0,.55)",
        "0 14px 34px -12px rgba(0,0,0,.6)",
        "0 22px 50px -16px rgba(0,0,0,.66)",
      ]
    : [
        "none",
        "0 2px 6px -3px rgba(20,40,80,.10)",
        "0 8px 20px -10px rgba(20,40,80,.14)",
        "0 12px 30px -12px rgba(20,40,80,.16)",
        "0 22px 48px -18px rgba(20,40,80,.20)",
      ];

  const theme = createTheme(
    {
      // ---- expõe tokens custom + modo ----
      mode,
      aurora: { ...c, radius: RADIUS, motion: MOTION, elev: sh, alpha: a, primary },

      // ---- COMPAT: logos / nome (consumidos por App.js e layout) ----
      appLogoLight: legacy.appLogoLight,
      appLogoDark: legacy.appLogoDark,
      appLogoFavicon: legacy.appLogoFavicon,
      appName: legacy.appName,
      calculatedLogoLight:
        legacy.calculatedLogoLight || (() => legacy.appLogoLight),
      calculatedLogoDark:
        legacy.calculatedLogoDark || (() => legacy.appLogoDark),

      // ---- COMPAT: scrollbars usados como theme.scrollbarStyles ----
      scrollbarStyles: {
        "&::-webkit-scrollbar": { width: "9px", height: "9px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: c.scroll,
          borderRadius: "10px",
          border: "2px solid transparent",
          backgroundClip: "content-box",
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },
      scrollbarStylesSoft: {
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: dark ? "#2b3a57" : "#dce3ee",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },

      shape: { borderRadius: RADIUS.md },

      palette: {
        type: mode, // v4: "light" | "dark"
        primary: { main: primary, contrastText: "#ffffff" },
        secondary: { main: dark ? "#7aa2ff" : "#4f46e5" },
        success: { main: "#16a34a" },
        warning: { main: "#d97706" },
        error: { main: "#dc2626" },
        info: { main: primary },
        background: { default: c.appBg, paper: c.paper },
        divider: c.border,
        text: { primary: c.text1, secondary: c.text2, disabled: c.text3 },
        action: {
          hover: a(primary, 0.07),
          selected: a(primary, 0.13),
          focus: a(primary, 0.18),
        },

        // ---- COMPAT: chaves customizadas usadas pelo código existente ----
        textPrimary: primary,
        borderPrimary: primary,
        dark: { main: dark ? "#F3F3F3" : "#333333" },
        light: { main: dark ? "#333333" : "#F3F3F3" },
        fontColor: primary,
        tabHeaderBackground: c.paper2,
        optionsBackground: c.paper2,
        fancyBackground: c.appBg,
        total: c.paper,
        messageIcons: dark ? "#F3F3F3" : "grey",
        inputBackground: c.paper2,
        barraSuperior: c.glass,
      },

      typography: {
        fontFamily:
          "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        h1: { fontSize: "2rem", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-.02em" },
        h2: { fontSize: "1.625rem", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-.015em" },
        h3: { fontSize: "1.375rem", fontWeight: 700, lineHeight: 1.3 },
        h4: { fontSize: "1.15rem", fontWeight: 700, lineHeight: 1.35 },
        h5: { fontSize: "1rem", fontWeight: 700, lineHeight: 1.4 },
        h6: { fontSize: ".9375rem", fontWeight: 700, lineHeight: 1.4 },
        subtitle1: { fontSize: ".875rem", fontWeight: 600 },
        subtitle2: { fontSize: ".8125rem", fontWeight: 600, color: c.text2 },
        body1: { fontSize: ".875rem", lineHeight: 1.55 },
        body2: { fontSize: ".8125rem", lineHeight: 1.5, color: c.text2 },
        caption: { fontSize: ".6875rem", color: c.text3 },
        button: { fontSize: ".8125rem", fontWeight: 600, textTransform: "none", letterSpacing: 0 },
      },

      props: {
        MuiButton: { disableElevation: true },
        MuiTextField: { variant: "outlined", size: "small" },
      },

      overrides: {
        MuiCssBaseline: {
          "@global": {
            "*::-webkit-scrollbar": { width: 9, height: 9 },
            "*::-webkit-scrollbar-thumb": {
              background: c.scroll,
              borderRadius: 10,
              border: "2px solid transparent",
              backgroundClip: "content-box",
            },
            "*::-webkit-scrollbar-track": { background: "transparent" },
            "*": { scrollbarWidth: "thin", scrollbarColor: `${c.scroll} transparent` },
            body: { background: c.appBg },
          },
        },

        // COMPAT: várias telas dependem de Container full-width (override original)
        MuiContainer: {
          root: {
            paddingLeft: "0 !important",
            paddingRight: "0 !important",
            maxWidth: "none !important",
            width: "100% !important",
          },
          maxWidthLg: { maxWidth: "none !important" },
          maxWidthMd: { maxWidth: "none !important" },
          maxWidthSm: { maxWidth: "none !important" },
          maxWidthXl: { maxWidth: "none !important" },
          maxWidthXs: { maxWidth: "none !important" },
        },

        MuiPaper: {
          root: { backgroundImage: "none" },
          rounded: { borderRadius: RADIUS.lg },
          outlined: { border: `1px solid ${c.border}` },
          elevation1: { boxShadow: sh[1] },
          elevation2: { boxShadow: sh[2] },
          elevation3: { boxShadow: sh[3] },
          elevation4: { boxShadow: sh[3] },
          elevation8: { boxShadow: sh[4] },
        },

        MuiCard: { root: { borderRadius: RADIUS.lg, border: `1px solid ${c.border}`, boxShadow: sh[1] } },

        MuiButton: {
          root: { borderRadius: RADIUS.sm, padding: "8px 16px", transition: `all ${MOTION}` },
          containedPrimary: {
            background: `linear-gradient(135deg, ${primary}, ${a(primary, 0.78)})`,
            boxShadow: `0 6px 16px -6px ${a(primary, 0.55)}`,
            "&:hover": { boxShadow: `0 8px 20px -6px ${a(primary, 0.6)}`, filter: "brightness(1.04)" },
          },
          outlined: { "&:hover": { borderColor: primary, backgroundColor: a(primary, 0.05) } },
          text: { "&:hover": { backgroundColor: a(primary, 0.08) } },
        },

        MuiIconButton: {
          root: {
            borderRadius: RADIUS.sm,
            transition: `all ${MOTION}`,
          },
        },

        MuiOutlinedInput: {
          root: {
            borderRadius: RADIUS.md,
            backgroundColor: c.paper2,
            transition: `box-shadow ${MOTION}, border-color ${MOTION}`,
            "& fieldset": { borderColor: c.border },
            "&:hover fieldset": { borderColor: c.text3 },
            "&.Mui-focused fieldset": { borderColor: primary, borderWidth: 1 },
            "&.Mui-focused": { boxShadow: `0 0 0 3px ${a(primary, 0.15)}` },
          },
        },

        MuiTabs: { indicator: { height: 3, borderRadius: 3, backgroundColor: primary } },
        MuiTab: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: ".8125rem",
            minHeight: 44,
            "&.Mui-selected": { color: primary },
          },
        },

        MuiChip: {
          root: { borderRadius: RADIUS.sm, fontWeight: 600 },
          outlined: { borderColor: c.border },
          colorPrimary: { backgroundColor: a(primary, 0.13), color: primary },
        },

        MuiBadge: { badge: { fontWeight: 700 } },

        MuiDialog: { paper: { borderRadius: RADIUS.lg, border: `1px solid ${c.border}`, boxShadow: sh[4] } },
        MuiDialogTitle: { root: { fontWeight: 700, fontSize: "1.05rem" } },

        MuiMenu: { paper: { borderRadius: RADIUS.md, border: `1px solid ${c.border}`, boxShadow: sh[4] } },
        MuiMenuItem: { root: { fontSize: ".8125rem", borderRadius: RADIUS.sm, margin: "2px 6px", "&:hover": { backgroundColor: a(primary, 0.08) } } },

        MuiTooltip: { tooltip: { backgroundColor: c.text1, color: c.paper, fontSize: ".6875rem", borderRadius: 8, padding: "6px 10px" } },

        MuiTableCell: {
          root: { borderBottom: `1px solid ${c.border}`, fontSize: ".8125rem" },
          head: { fontWeight: 700, color: c.text2, fontSize: ".6875rem", letterSpacing: ".06em", textTransform: "uppercase" },
        },

        MuiAvatar: { root: { fontWeight: 700, fontSize: ".875rem" } },
        MuiDivider: { root: { backgroundColor: c.border } },

        MuiAppBar: {
          root: {
            background: c.glass,
            backdropFilter: "blur(20px) saturate(1.4)",
            WebkitBackdropFilter: "blur(20px) saturate(1.4)",
            borderBottom: `1px solid ${c.border}`,
            color: c.text1,
            boxShadow: "none",
          },
        },
        MuiDrawer: { paper: { backgroundColor: c.paper, borderRight: `1px solid ${c.border}` } },
      },
    },
    legacy.locale
  );

  return theme;
}
